/* The Detail Reserve — detail builder, sheets, gallery and appointment request.
   One shared selection (S) drives both paths: "Help me choose" and "All packages".
   Prices and rules come from the section's JSON (edited in the theme editor). */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const smooth = reduce ? 'auto' : 'smooth';
  const money = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });
  const track = (event, data = {}) => {
    (window.dataLayer = window.dataLayer || []).push({ event, ...data });
    // Requests are measured once, in tdr-track.js (no names, phone numbers or emails).
    if (event === 'generate_lead' && window.tdrTrack) {
      window.tdrTrack('generate_lead', { request_type: document.querySelector('[name="contact[Request type]"]')?.value || '', test: Boolean(data.tdr_test) });
    }
  };
  const body = document.body;

  // ---- Sheets (package details, compare) --------------------------------------
  const openSheet = (dlg) => {
    if (!dlg || typeof dlg.showModal !== 'function') return;
    dlg.showModal();
    body.classList.add('has-sheet');
  };
  $$('dialog[data-sheet]').forEach((dlg) => {
    dlg.addEventListener('close', () => { if (!$('dialog[data-sheet][open]')) body.classList.remove('has-sheet'); });
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg) dlg.close(); // tap on the backdrop
      if (e.target.closest('[data-close]')) dlg.close();
    });
  });

  // ---- Builder ----------------------------------------------------------------
  const root = $('[data-builder]');
  const est = root && $('[data-est]', root);
  let S = null;
  const D = root ? JSON.parse($('[data-tdr-data]', root).textContent) : null;

  if (root) {
    const fresh = () => ({ mode: 'guide', step: 0, vehicle: null, condition: null, goal: null, concerns: [], pkg: null, path: null, rec: null, alt: '', quote: false, why: [], suggested: [], addons: {}, opts: {}, popt: {}, zip: '', zone: '', showResult: false });
    S = fresh();
    // Each service page keeps its own selection. The version drops selections saved under an older menu.
    const storeKey = `tdr-build-v2-${location.pathname}`;
    try { Object.assign(S, JSON.parse(sessionStorage.getItem(storeKey) || '{}')); } catch (_) { /* no storage */ }
    S.popt = S.popt || {};
    const save = () => { try { sessionStorage.setItem(storeKey, JSON.stringify(S)); } catch (_) { /* no storage */ } };

    const pkg = (k) => D.packages.find((p) => p.key === k);
    const tier = (k) => { const p = pkg(k); return p && typeof p.rank === 'number' ? p.rank : Math.max(0, D.packages.findIndex((x) => x.key === k)); };
    const byTier = (t) => D.packages.find((p) => tier(p.key) === t) || D.packages[t];
    const vehicle = () => D.vehicles.find((v) => v.key === S.vehicle);
    const condition = () => D.conditions.find((c) => c.key === S.condition);
    const addonDef = (k) => D.addons.find((a) => a.key === k);
    const row = (k) => $(`[data-ao="${CSS.escape(k)}"]`, root);

    // ---------- Pricing (no invented surcharges: no verified price for a size = "confirmed in quote")
    // Package options, one per line as "Label|price|hint". "# Heading|kind" starts a new choice group
    // (kind "finish" = the paint-finish choice). Price: a number, per size as "car/mid/large", a "+"
    // suffix for a starting price, or "quote" (priced after inspection, never added to the total).
    // Options in a group replace each other, so an upgrade is never charged on top of what it replaces.
    // One price format everywhere: "300", per class "300/350/400/500", "400+" (starting price), "450-750" (range) or "quote".
    const parsePrice = (p = '') => {
      const t = String(p).trim();
      const r = t.match(/^(\d+)-(\d+)$/);
      if (r) return { quote: false, from: false, prices: [Number(r[1])], max: Number(r[2]) };
      return { quote: /^quote$/i.test(t) || t === '', from: /\+$/.test(t), prices: t.replace(/\+$/, '').split('/').map((n) => Number(n) || 0) };
    };
    // Vehicles marked "confirm" (Not sure) are priced at class A and flagged until we confirm the class.
    const sizeIndex = () => { const v = vehicle(); return v && !v.confirm ? Math.max(0, D.vehicles.indexOf(v)) : 0; };
    const sizedPrice = (pp) => pp.prices[Math.min(sizeIndex(), pp.prices.length - 1)];
    const pkgGroups = (key) => {
      const groups = [];
      (pkg(key).options || []).forEach((raw) => {
        const l = (raw || '').trim();
        if (l.startsWith('#')) { const [label, kind] = l.slice(1).split('|'); groups.push({ label: label.trim(), kind: (kind || '').trim(), opts: [] }); return; }
        if (!l.includes('|')) return;
        if (!groups.length) groups.push({ label: D.optionLabel, kind: '', opts: [] });
        const [label, p = '', hint = ''] = l.split('|').map((x) => x.trim());
        groups[groups.length - 1].opts.push({ label, hint, ...parsePrice(p) });
      });
      return groups.filter((g) => g.opts.length);
    };
    const picked = (key) => { const v = S.popt[key]; return Array.isArray(v) ? v : [Number(v) || 0]; };
    const picks = (key) => pkgGroups(key).map((g, i) => ({ g, i, o: g.opts[picked(key)[i] || 0] || g.opts[0] }));
    const optPrice = (o) => (o.quote ? 0 : sizedPrice(o));
    const optText = (o) => (o.quote ? 'priced after inspection' : o.max ? `+${money(optPrice(o))}–${money(o.max)}` : optPrice(o) ? `+${o.from ? 'from ' : ''}${money(optPrice(o))}` : '');
    const sizePrice = (key) => { const v = vehicle(); return v && pkg(key).prices && pkg(key).prices[v.key] != null ? pkg(key).prices[v.key] : null; };
    // Some classes are priced as a range (e.g. Quick Refresh C/D): the estimate uses the low end and shows the range.
    const rangeTop = (key) => { const v = vehicle(); const mx = pkg(key).maxes || {}; const k = v && !v.confirm ? v.key : 'car'; return mx[k] != null ? mx[k] : null; };
    const knownFor = (key) => { const v = vehicle(); return !v || (!v.confirm && (sizePrice(key) != null || typeof v.adjust === 'number')); };
    const priceFor = (key, withOption = true) => {
      const v = vehicle();
      const explicit = sizePrice(key);
      const base = explicit != null ? explicit : pkg(key).price + (v && typeof v.adjust === 'number' ? v.adjust : 0);
      return base + (withOption ? picks(key).reduce((n, x) => n + optPrice(x.o), 0) : 0);
    };
    // Condition answers place an estimate inside an approved range (low / middle / high end); never outside it.
    // The middle rounds to the nearest $25 (e.g. $250–$300 → $275, $280–$325 → $300).
    const POS = { low: 0, mid: 0.5, high: 1 };
    const placeIn = (lo, hi, f) => Math.min(hi, Math.max(lo, Math.round((lo + f * (hi - lo)) / 25) * 25));
    const estimate = () => {
      if (!S.pkg) return null;
      const v = vehicle();
      const sizeUnknown = !v;
      const sizePending = v && !knownFor(S.pkg);
      const ps = picks(S.pkg);
      // Included choices read as part of the package line; priced or quoted choices get their own line.
      const inline = ps.filter((x) => !x.o.quote && !optPrice(x.o)).map((x) => x.o.label);
      const top = rangeTop(S.pkg);
      // pos: lines whose range depends on condition (the package's own range, and the paint-finish choice).
      const lines = [{ label: [pkg(S.pkg).name, ...inline].join(' · '), price: priceFor(S.pkg, false), max: top, from: pkg(S.pkg).from, pos: true }];
      ps.filter((x) => x.o.quote || optPrice(x.o)).forEach((x) => lines.push({ label: `${x.g.label}: ${x.o.label}`, price: optPrice(x.o), max: x.o.max || null, quote: x.o.quote, from: x.o.from, opt: true, pos: x.g.kind === 'finish' }));
      let from = sizeUnknown || sizePending || lines.some((l) => l.from);
      Object.keys(S.addons).filter((k) => S.addons[k]).forEach((k) => {
        const a = addonDef(k);
        if (!a || a.included.includes(S.pkg) || (a.only.filter(Boolean).length && !a.only.includes(S.pkg))) return;
        if (a.quote) { lines.push({ label: a.name, price: 0, quote: true }); from = true; return; }
        const optEl = $(`[data-ao="${CSS.escape(k)}"] [data-ao-opts] input:checked`, root);
        if (row(k)?.hidden) return;
        const pp = parsePrice(optEl ? optEl.dataset.price : a.price);
        const isFrom = a.from || pp.from;
        lines.push({ label: a.name + (optEl ? ` (${optEl.value.toLowerCase()})` : ''), price: sizedPrice(pp), max: pp.max || null, from: isFrom });
        if (isFrom) from = true;
      });
      const total = lines.reduce((n, l) => n + l.price, 0);
      const maxTotal = lines.reduce((n, l) => n + (l.max || l.price), 0);
      const max = maxTotal > total ? maxTotal : null;
      const quoted = lines.filter((l) => l.quote).map((l) => l.label);
      const c = condition();
      const f = c ? POS[c.position] : undefined;
      const point = f != null && lines.some((l) => l.max && l.pos) ? lines.reduce((n, l) => n + (l.max && l.pos ? placeIn(l.price, l.max, f) : l.price), 0) : null;
      let note = 'An estimate — your final price is confirmed with you before any work starts.';
      if (sizeUnknown) note = 'Shown at the car / small SUV price. Choose your vehicle size for a closer estimate.';
      else if (sizePending) note = 'Shown at the class A (car) price. We confirm your vehicle class from its year, make and model and explain any change before booking.';
      else if (from) note = pkg(S.pkg).from ? 'Starting price. Condition can affect it; your final price is confirmed before any work starts.' : 'Some add-ons depend on condition. Your final price is confirmed before any work starts.';
      if (max && !top) note = 'Shown as a range: where your price falls is confirmed after we see the vehicle, before any work starts.';
      if (max && top) note = `Starting at ${money(lines[0].price)} for well-maintained ${v && v.key === 'large' ? 'XL vehicles and duallies' : 'vehicles'}. Final price may increase up to ${money(top)} when additional cleaning time is needed based on size and condition.`;
      if (point != null) note = `Estimated from ${money(point)} based on ${c.basis || 'the condition you chose'}. Estimated ${money(total)}–${money(max)} depending on final condition.`;
      // Anything priced after inspection makes this a subtotal of the known items, never a complete total.
      // Detailing visit minimum: disclosed, never added on top of the package total.
      if (pkg(S.pkg).visitMin && D.visitMinimum && (point != null ? point : total) < 300) note = `${note} ${D.visitMinimum}`;
      if (quoted.length) note = 'Subtotal of the priced items only. Anything marked “Priced after inspection” is confirmed once we see the vehicle, before you book.';
      return { lines, total, max, point, basis: c && c.basis, from, partial: quoted.length > 0, note };
    };
    const rangeOf = (e) => (e.max ? `${money(e.total)}–${money(e.max)}` : money(e.total));
    const amount = (e) => (e.point != null ? money(e.point) : rangeOf(e));
    const kindOf = (e) => (e.partial ? 'subtotal' : e.point != null ? 'est.' : e.max ? 'range' : e.from ? 'from' : 'estimate');
    const priceText = (e) => (e.point != null ? `est. from ${money(e.point)}` : `${e.from && !e.max ? 'from ' : ''}${amount(e)}`) + (e.partial ? ' + quote' : '');
    // Estimate for another package (or another configuration) without changing what the customer has chosen.
    const estimateFor = (key, popt) => {
      const keepPkg = S.pkg; const keepOpt = S.popt[key];
      S.pkg = key; if (popt) S.popt[key] = popt;
      const e = estimate();
      S.pkg = keepPkg; if (popt) S.popt[key] = keepOpt;
      return e;
    };
    // Nearby options under the recommendation: at most one simpler and one deeper, only when the answers make them suitable.
    const alternatives = () => {
      if (!S.rec) return [];
      const order = D.packages.slice().sort((a, b) => tier(a.key) - tier(b.key)).map((x) => x.key);
      const i = order.indexOf(S.rec);
      const c = condition();
      const minTier = c ? tier(c.min) : 0;
      const out = [];
      const groups = pkgGroups(S.rec);
      const sel = picked(S.rec);
      if (groups.length && groups[0].kind !== 'finish' && (sel[0] || 0) > 0) {
        // Same package, simpler configuration (e.g. base protection instead of a longer coating).
        const popt = sel.slice(); popt[0] = 0;
        out.push({ kind: 'down', key: S.rec, popt, label: 'Want a simpler option?', name: `${pkg(S.rec).name} · ${groups[0].opts[0].label}`, why: 'The same package with the base protection instead of a longer coating.' });
      } else if (i > 0 && tier(order[i - 1]) >= minTier) {
        out.push({ kind: 'down', key: order[i - 1], label: 'Want a simpler option?', name: pkg(order[i - 1]).name, why: pkg(order[i - 1]).altWhy });
      }
      // Deeper option only when the condition calls for more than the basics, never for a well-kept vehicle.
      const up = order[i + 1];
      if (up && pkg(up).altUp && (!c || c.position !== 'low')) out.push({ kind: 'up', key: up, label: pkg(up).altUp, name: pkg(up).name, why: pkg(up).altWhy });
      return out.map((a) => ({ ...a, price: priceText(estimateFor(a.key, a.popt)).replace(' + quote', '') }));
    };

    // ---------- Mode tabs
    const seg = $('[data-seg]', root);
    const tabs = $$('[role="tab"]', seg);
    const setMode = (mode) => {
      S.mode = mode;
      seg.dataset.active = mode;
      tabs.forEach((t) => { const on = t.dataset.tab === mode; t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; });
      $$('[data-pane]', root).forEach((p) => { p.hidden = p.dataset.pane !== mode; });
      save();
    };
    tabs.forEach((t) => {
      t.addEventListener('click', () => { setMode(t.dataset.tab); track('builder_mode', { tdr_mode: t.dataset.tab }); });
      t.addEventListener('keydown', (e) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
        const other = tabs.find((x) => x !== t);
        other.focus(); other.click();
      });
    });
    document.addEventListener('click', (e) => {
      const start = e.target.closest('[data-start]');
      if (start && start.getAttribute('href')?.startsWith('#')) setMode(start.dataset.start);
    });

    // ---------- Guided path
    const guide = $('[data-guide]', root);
    const steps = ['vehicle', 'condition', 'goal', 'concerns'];
    const qs = steps.map((k) => $(`[data-q="${k}"]`, guide));
    const result = $('[data-result]', guide);
    const back = $('[data-g-back]', guide);
    const keepInView = () => {
      const top = guide.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.4) guide.scrollIntoView({ behavior: smooth, block: 'start' });
    };
    // A question that already has an answer (e.g. after Back) shows Next, so it can be kept as it is.
    const syncNext = (i) => { const w = qs[i] && $('[data-g-next]', qs[i]); if (w) w.hidden = !$('input:checked', qs[i]); };
    const showStep = (i) => {
      S.step = i; S.showResult = false;
      qs.forEach((q, n) => q.classList.toggle('is-on', n === i));
      result.hidden = true;
      $('[data-g-count]', guide).textContent = `Question ${i + 1} of ${qs.length}`;
      $$('[data-g-bar] span', guide).forEach((b, n) => b.classList.toggle('is-done', n <= i));
      back.hidden = i === 0;
      syncNext(i);
      save();
    };

    const recommend = (applyChoices = true) => {
      const mins = [];
      const c = condition(); if (c) mins.push({ k: c.min, r: c.reason });
      const g = D.goals.find((x) => x.key === S.goal); if (g) mins.push({ k: g.min, r: g.reason });
      S.concerns.forEach((key) => { const x = D.concerns.find((cc) => cc.key === key); if (x && x.min) mins.push({ k: x.min, r: x.reason }); });
      const best = mins.reduce((t, m) => Math.max(t, tier(m.k)), 0);
      S.rec = byTier(best).key;
      // Heavier condition plus stains/odors (or several condition concerns): a condition-based quote, not a fixed package.
      const qc = c && (c.quoteConcerns || []).filter(Boolean);
      S.quote = Boolean(c && ((qc.length && S.concerns.some((k) => qc.includes(k))) || (c.quoteCount && S.concerns.length >= c.quoteCount)));
      S.why = [...new Set(mins.filter((m) => m.r && tier(m.k) === best).map((m) => m.r))];
      // Answers can pick a package choice ("group:option"), e.g. "About 3 years" → the 3-year upgrade.
      // Only choices the customer asked for; paid correction is never pre-selected from a condition.
      const groups = pkgGroups(S.rec);
      const sel = picked(S.rec).slice();
      [c, g].forEach((a) => {
        if (!a || !a.option) return;
        const [gi, oi] = a.option.split(':').map(Number);
        if (groups[gi] && groups[gi].opts[oi]) sel[gi] = oi;
      });
      if (groups.length && applyChoices) S.popt[S.rec] = sel;
      // Concerns the customer told us about become suggested add-ons: shown with their price, switched off.
      // Paid extras are never pre-selected; the customer turns them on.
      const offered = (a) => a && !a.included.includes(S.rec) && (!a.only.filter(Boolean).length || a.only.includes(S.rec));
      S.suggested = S.concerns.map((key) => D.concerns.find((cc) => cc.key === key)).filter((x) => x && x.addon && offered(addonDef(x.addon))).map((x) => ({ addon: x.addon, because: x.label }));
    };

    const renderResult = () => {
      const p = pkg(S.rec);
      $('[data-r-name]', result).textContent = S.quote ? 'Condition-based quote' : p.name;
      const e = (() => { const keep = S.pkg; S.pkg = S.rec; const x = estimate(); S.pkg = keep; return x; })();
      $('[data-r-price]', result).textContent = S.quote ? 'Priced after photos or an inspection' : priceText(e).replace(' + quote', '');
      const rMeta = e.point != null && e.max ? `estimated ${rangeOf(e)} depending on final condition` : '';
      $('[data-r-meta]', result).textContent = S.quote ? `Closest package: ${p.name}, ${priceText(e).replace(' + quote', '')} for normal condition` : [p.time, rMeta, e.partial ? 'subtotal; some choices priced after inspection' : '', S.suggested.some((x) => S.addons[x.addon]) ? 'incl. add-ons you chose' : ''].filter(Boolean).join(' · ');
      const choices = $('[data-r-choices]', result);
      if (choices) {
        choices.innerHTML = '';
        picks(S.rec).forEach((x) => { const li = document.createElement('li'); li.textContent = `${x.g.label}: ${x.o.label}`; choices.append(li); });
        choices.hidden = !choices.children.length;
      }
      $('[data-r-why]', result).innerHTML = '';
      S.why.forEach((w) => { const li = document.createElement('li'); li.textContent = w; $('[data-r-why]', result).append(li); });
      const hl = $('[data-r-highlights]', result); hl.innerHTML = '';
      p.highlights.filter(Boolean).forEach((h) => { const li = document.createElement('li'); li.textContent = h.trim(); hl.append(li); });
      const wrap = $('[data-r-added-wrap]', result);
      const list = $('[data-r-added]', result);
      list.innerHTML = '';
      wrap.hidden = !S.suggested.length;
      S.suggested.forEach((s) => {
        const a = addonDef(s.addon);
        const row = document.createElement('label');
        row.className = 'added__row';
        row.innerHTML = `<span><strong></strong><small></small></span><span class="switch"><input type="checkbox"><span></span></span>`;
        const ap = parsePrice(a.price);
        $('strong', row).textContent = a.quote ? `${a.name} · priced on inspection` : `${a.name} · ${a.from || ap.from ? 'from ' : ''}+${money(sizedPrice(ap))}`;
        $('small', row).textContent = `Because you mentioned ${s.because.toLowerCase()}`;
        const input = $('input', row);
        input.checked = Boolean(S.addons[a.key]);
        input.setAttribute('aria-label', `Include ${a.name}`);
        input.addEventListener('change', () => { S.addons[a.key] = input.checked; save(); renderResult(); render(); });
        list.append(row);
      });
      $('[data-r-continue]', result).textContent = S.quote ? 'Continue to a condition-based quote' : `Continue with ${p.name}`;
      const altBox = $('[data-r-alts]', result);
      if (altBox) {
        const list = $('[data-r-alts-list]', altBox);
        list.innerHTML = '';
        const alts = S.quote ? [] : alternatives();
        alts.forEach((a) => {
          const card = document.createElement('div');
          card.className = 'alt';
          card.innerHTML = '<span class="alt__label"></span><strong class="alt__name"></strong><span class="alt__why"></span><span class="alt__price num"></span><button type="button" class="btn btn--line btn--sm"></button>';
          $('.alt__label', card).textContent = a.label;
          $('.alt__name', card).textContent = a.name;
          $('.alt__why', card).textContent = a.why || '';
          $('.alt__price', card).textContent = a.price;
          const btn = $('button', card);
          btn.textContent = `Choose ${pkg(a.key).name}`;
          btn.addEventListener('click', () => {
            if (a.popt) S.popt[a.key] = a.popt.slice();
            S.alt = `${a.kind === 'down' ? 'simpler' : 'deeper'} option: ${a.name}`;
            track('guide_alternative', { tdr_package: a.key, tdr_alt: a.kind });
            selectPackage(a.key, `Help me choose · ${S.alt}`);
          });
          list.append(card);
        });
        altBox.hidden = !alts.length;
      }
      // One related-service suggestion in the recommendation, based on the answers.
      const rTokens = new Set(['any', S.rec, S.condition, S.goal, S.vehicle, ...S.concerns].filter(Boolean));
      const rUp = $('[data-r-up]', result);
      if (rUp) {
        rUp.innerHTML = '';
        const match = $$('[data-ups] .up', root).find((u) => (u.dataset.upWhen || '').split(/\s+/).some((t) => rTokens.has(t) && t !== 'any') && !(u.dataset.upHide || '').split(/\s+/).includes(S.rec));
        if (match) { const c = match.cloneNode(true); c.hidden = false; rUp.append(c); }
        $('[data-r-up-wrap]', result).hidden = !match;
      }
    };

    const showResult = (restore = false) => {
      recommend(!restore);
      S.showResult = true;
      qs.forEach((q) => q.classList.remove('is-on'));
      $('[data-g-count]', guide).textContent = 'Your recommendation';
      $$('[data-g-bar] span', guide).forEach((b) => b.classList.add('is-done'));
      back.hidden = false;
      renderResult();
      result.hidden = false;
      result.focus({ preventScroll: true });
      keepInView();
      render();
      save();
      track('guide_result', { tdr_package: S.rec });
    };

    // Tap/click on an answer moves straight on. With the keyboard, arrow keys only change the answer
    // (a radio group selects as you arrow through it) and Enter continues, so nothing is skipped.
    let viaKeys = false;
    guide.addEventListener('keydown', () => { viaKeys = true; }, true);
    guide.addEventListener('pointerdown', () => { viaKeys = false; }, true);
    const advance = (i) => {
      if (S.showResult || S.step !== i) return; // a late auto-advance or a double tap never skips a question
      showStep(i + 1); keepInView();
      const next = qs[i + 1];
      ($('input:checked', next) || $('input', next))?.focus({ preventScroll: true });
    };
    guide.addEventListener('change', (e) => {
      const t = e.target;
      if (t.name === 'g-vehicle') { S.vehicle = t.value; }
      if (t.name === 'g-condition') { S.condition = t.value; }
      if (t.name === 'g-goal') { S.goal = t.value; }
      if (t.name === 'g-concern') { S.concerns = $$('input[name="g-concern"]:checked', guide).map((i) => i.value); save(); return; }
      save(); render();
      const i = steps.indexOf(t.name.replace('g-', ''));
      if (i > -1) syncNext(i);
      if (!viaKeys && i > -1 && i < steps.length - 1) setTimeout(() => advance(i), reduce ? 0 : 240);
    });
    guide.addEventListener('keydown', (e) => {
      const t = e.target;
      if (e.key !== 'Enter' || t.type !== 'radio' || !t.checked) return;
      const i = steps.indexOf(t.name.replace('g-', ''));
      if (i > -1 && i < steps.length - 1) { e.preventDefault(); advance(i); }
    });
    $('[data-g-finish]', guide).addEventListener('click', () => showResult());
    guide.addEventListener('click', (e) => { if (e.target.closest('[data-g-next] button')) advance(S.step); });
    back.addEventListener('click', () => {
      showStep(S.showResult ? steps.length - 1 : Math.max(0, S.step - 1)); keepInView();
      // Back disappears on the first question: move focus to that question's answer instead of losing it.
      if (back.hidden) ($('input:checked', qs[S.step]) || $('input', qs[S.step]))?.focus({ preventScroll: true });
      // Bring Next clear of the phone call/text bar, but never push the question under the sticky header.
      const w = $('[data-g-next]', qs[S.step]);
      if (w && !w.hidden) setTimeout(() => {
        const head = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 64;
        const by = Math.min(w.getBoundingClientRect().bottom - (window.innerHeight - 88), $('[data-g-count]', guide).getBoundingClientRect().top - head - 8);
        if (by > 0) window.scrollBy({ top: by, behavior: smooth });
      }, reduce ? 0 : 350);
    });
    $('[data-r-continue]', result).addEventListener('click', () => { S.alt = ''; selectPackage(S.rec, S.quote ? 'Help me choose · condition-based quote' : 'Help me choose'); });
    $('[data-r-compare]', result).addEventListener('click', () => { setMode('packages'); openSheet($('#compare')); });
    $('[data-r-details]', result).addEventListener('click', () => openSheet($(`#pd-${CSS.escape(S.rec)}`)));
    $('[data-r-restart]', result).addEventListener('click', () => {
      S.condition = null; S.goal = null; S.concerns = []; S.rec = null; S.suggested = [];
      $$('input[name^="g-"]', guide).forEach((i) => { if (i.name !== 'g-vehicle') i.checked = false; });
      showStep(0); render();
    });

    // ---------- Packages + selection
    const cus = $('[data-cus]', root);
    const selectPackage = (key, path) => {
      S.pkg = key;
      S.path = path || S.path || 'All packages';
      cus.hidden = false;
      render(); save();
      track('package_selected', { tdr_package: key, tdr_path: S.path });
      requestAnimationFrame(() => cus.scrollIntoView({ behavior: smooth, block: 'start' }));
    };
    root.addEventListener('click', (e) => {
      const sel = e.target.closest('[data-select]');
      if (sel) { S.alt = ''; selectPackage(sel.dataset.select, sel.closest('#compare') ? 'Compared packages' : 'All packages'); }
      const det = e.target.closest('[data-details]');
      if (det) { openSheet($(`#pd-${CSS.escape(det.dataset.details)}`)); track('package_details', { tdr_package: det.dataset.details }); }
      if (e.target.closest('[data-open-compare]')) { openSheet($('#compare')); track('package_compare'); }
    });

    // ---------- Customize
    $('[data-c-change]', root).addEventListener('click', () => root.scrollIntoView({ behavior: smooth, block: 'start' }));
    $('[data-c-fit-switch]', root).addEventListener('click', (e) => {
      const c = condition();
      const fi = Number(e.currentTarget.dataset.finish);
      if (e.currentTarget.dataset.finish) { const sel = picked(S.pkg).slice(); sel[fi] = pkgGroups(S.pkg)[fi].opts.length - 1; S.popt[S.pkg] = sel; save(); render(); return; }
      if (c) selectPackage(c.min, S.path);
    });
    root.addEventListener('change', (e) => {
      const t = e.target;
      if (t.name === 'c-vehicle') { S.vehicle = t.value; const g = $(`input[name="g-vehicle"][value="${CSS.escape(t.value)}"]`, guide); if (g) g.checked = true; }
      if (t.dataset.addon) {
        S.addons[t.dataset.addon] = t.checked;
        // Either-or add-ons (e.g. spray vs professional wheel coating) never stack on the same surfaces.
        const g = addonDef(t.dataset.addon)?.group;
        if (t.checked && g) D.addons.filter((x) => x.group === g && x.key !== t.dataset.addon).forEach((x) => { S.addons[x.key] = false; });
      }
      if (t.name && t.name.startsWith('opt-')) S.opts[t.name.slice(4)] = t.value;
      const isPopt = (t.name || '').startsWith('c-popt-');
      if (isPopt) { const sel = picked(S.pkg).slice(); sel[Number(t.name.slice(7))] = Number(t.value); S.popt[S.pkg] = sel; track('package_option', { tdr_package: S.pkg, tdr_option: t.parentNode.dataset.label || '' }); }
      if (t.name === 'c-vehicle' || isPopt || t.dataset.addon || (t.name || '').startsWith('opt-')) { save(); render(); }
    });
    $$('.ao__name', root).forEach((b) => b.addEventListener('click', () => {
      const open = b.getAttribute('aria-expanded') !== 'true';
      b.setAttribute('aria-expanded', String(open));
      $(`#${b.getAttribute('aria-controls')}`).hidden = !open;
    }));

    // ---------- Service area
    const zip = $('[data-zip]', root);
    const zipRes = $('[data-zip-res]', root);
    const checkZip = () => {
      zip.value = zip.value.replace(/\D/g, '').slice(0, 5);
      S.zip = zip.value;
      if (zip.value.length < 5) { zipRes.hidden = true; S.zone = ''; save(); return; }
      const z = D.zones.find((x) => x.zips.includes(zip.value) || x.prefixes.some((p) => p && zip.value.startsWith(p)));
      zipRes.hidden = false;
      zipRes.className = `zip__res is-${z ? z.id : 'unknown'}`;
      zipRes.textContent = z ? z.label : D.unknownZone;
      S.zone = `${zip.value} · ${z ? z.id : 'outside listed zones'}`;
      const reqZip = $('[data-req-zip]');
      if (reqZip && !reqZip.value) reqZip.value = zip.value;
      save(); carry();
      track('zip_check', { tdr_zone: z ? z.id : 'unknown' });
    };
    zip.addEventListener('input', checkZip);

    // ---------- Estimate bar
    const estToggle = $('[data-est-toggle]', est);
    const estPanel = $('[data-est-panel]', est);
    estToggle.addEventListener('click', () => {
      const open = estToggle.getAttribute('aria-expanded') !== 'true';
      estToggle.setAttribute('aria-expanded', String(open));
      estPanel.hidden = !open;
    });
    // Hide the bar while the customize card itself is on screen on desktop (it already shows the button).
    let cusVisible = false;
    if ('IntersectionObserver' in window) new IntersectionObserver(([e]) => { cusVisible = e.isIntersecting && window.innerWidth >= 1000; render(); }, { threshold: 0.35 }).observe(cus);

    // ---------- Render everything from S
    const render = () => {
      const e = estimate();
      // Package cards
      $$('.pk', root).forEach((card) => {
        card.classList.toggle('is-sel', card.dataset.pk === S.pkg);
        $('[data-pk-rec]', card).hidden = card.dataset.pk !== S.rec;
        const lo = priceFor(card.dataset.pk, false); const hi = rangeTop(card.dataset.pk);
        $('[data-pk-price]', card).textContent = hi ? `${money(lo)}–${money(hi)}` : money(lo);
      });
      $$('#compare [data-col]').forEach((c) => c.classList.toggle('is-sel', c.dataset.col === S.pkg));
      $$('input[name="c-vehicle"]', root).forEach((i) => { i.checked = i.value === S.vehicle; });

      cus.hidden = !S.pkg;
      if (S.pkg) {
        $('[data-c-name]', root).textContent = `${pkg(S.pkg).name} · ${priceText(e)}`;
        const c = condition();
        const groups = pkgGroups(S.pkg);
        const fit = $('[data-c-fit]', root);
        const sw = $('[data-c-fit-switch]', fit);
        const tooLight = c && c.fit && tier(S.pkg) < tier(c.min);
        // Keeping the current finish on paint the customer described as swirled: say so honestly.
        const fi = groups.findIndex((g) => g.kind === 'finish');
        const keepsFinish = c && c.fit && fi > -1 && (picked(S.pkg)[fi] || 0) === 0;
        fit.hidden = !(tooLight || keepsFinish);
        if (tooLight || keepsFinish) {
          $('[data-c-fit-text]', fit).textContent = c.fit;
          sw.textContent = keepsFinish ? 'Ask us to advise' : `Switch to ${pkg(c.min).name}`;
          if (keepsFinish) sw.dataset.finish = String(fi); else delete sw.dataset.finish;
        }
        const minNote = $('[data-c-min]', root);
        if (minNote) minNote.hidden = !pkg(S.pkg).visitMin;
        // Package choices (coating term, paint finish…), one radio group each
        const ow = $('[data-c-popts-wrap]', root);
        ow.hidden = !groups.length;
        const box = $('[data-c-popts]', root);
        const sig = `${S.pkg}|${S.vehicle}`;
        if (groups.length && box.dataset.for !== sig) {
          box.dataset.for = sig;
          box.innerHTML = '';
          groups.forEach((g, gi) => {
            const fs = document.createElement('fieldset');
            fs.className = 'field popt';
            fs.innerHTML = '<legend class="label-sm"></legend><div class="pills"></div><p class="popt__hint small muted" aria-live="polite"></p>';
            fs.firstChild.textContent = g.label;
            g.opts.forEach((o, i) => {
              const l = document.createElement('label');
              l.dataset.label = o.label;
              l.innerHTML = '<input type="radio"><span></span>';
              l.firstChild.name = `c-popt-${gi}`;
              l.firstChild.value = String(i);
              l.lastChild.textContent = optText(o) ? `${o.label} · ${optText(o)}` : o.label;
              $('.pills', fs).append(l);
            });
            box.append(fs);
          });
        }
        groups.forEach((g, gi) => {
          const cur = picked(S.pkg)[gi] || 0;
          $$(`input[name="c-popt-${gi}"]`, box).forEach((i) => { i.checked = Number(i.value) === cur; });
          const hint = $$('.popt__hint', box)[gi];
          if (hint) hint.textContent = (g.opts[cur] || g.opts[0]).hint;
        });
        $$('.ao', root).forEach((row) => {
          const a = addonDef(row.dataset.ao);
          if (!a) { row.hidden = true; return; }
          row.hidden = Boolean(a.only.filter(Boolean).length && !a.only.includes(S.pkg));
          const input = $('[data-addon]', row);
          const inc = a.included.includes(S.pkg);
          row.classList.toggle('is-inc', inc);
          input.disabled = inc;
          input.checked = !inc && Boolean(S.addons[a.key]);
          const opts = $('[data-ao-opts]', row);
          if (opts) {
            opts.hidden = !input.checked;
            if (S.opts[a.key]) { const o = $(`input[value="${CSS.escape(S.opts[a.key])}"]`, opts); if (o) o.checked = true; }
          }
        });
      }

      // Related services: relevant to what the customer told us, never what's already included
      const tokens = new Set(['any', S.pkg, S.rec, S.condition, S.goal, S.vehicle, ...S.concerns].filter(Boolean));
      const upWrap = $('[data-ups]', root);
      if (upWrap) {
        let shown = 0;
        $$('.up', upWrap).forEach((u) => {
          const when = (u.dataset.upWhen || '').split(/\s+/).filter(Boolean);
          const hide = (u.dataset.upHide || '').split(/\s+/).filter(Boolean);
          const ok = shown < 2 && when.some((t) => tokens.has(t)) && !hide.includes(S.pkg);
          u.hidden = !ok;
          if (ok) shown += 1;
        });
        upWrap.hidden = !shown;
      }

      // Estimate bar
      const showBar = Boolean(S.pkg) && !cusVisible;
      est.classList.toggle('is-shown', showBar);
      body.classList.toggle('has-est', showBar);
      if (e) {
        const v = vehicle();
        $('[data-est-pkg]', est).textContent = `${pkg(S.pkg).name} · ${v ? v.short : 'size not set'}`;
        // Same estimate as the builder (estimate() above): "From $1,220", plus a tag only when it isn't a plain starting price.
        $('[data-est-total]', est).textContent = e.point != null ? `From ${money(e.point)}` : e.max ? amount(e) : `${e.from ? 'From ' : ''}${money(e.total)}`;
        $('[data-est-kind]', est).textContent = e.partial ? 'subtotal + quote' : e.point != null ? 'est.' : e.from || e.max ? '' : 'estimate';
        const lines = $('[data-est-lines]', est);
        lines.innerHTML = '';
        e.lines.forEach((l) => {
          const d = document.createElement('div');
          d.className = 'est__line';
          d.innerHTML = '<span></span><span class="num"></span>';
          d.firstChild.textContent = l.label;
          d.lastChild.textContent = l.quote ? 'Priced after inspection' : l.max ? `${money(l.price)}–${money(l.max)}` : `${l.from ? 'from ' : ''}${money(l.price)}`;
          lines.append(d);
        });
        $('[data-est-note]', est).textContent = e.note;
      }
      carry();
    };

    // ---------- Carry the selection into the request form
    const carry = () => {
      const e = estimate();
      const v = vehicle();
      const c = condition();
      const g = D.goals.find((x) => x.key === S.goal);
      const addons = e ? e.lines.slice(1).filter((l) => !l.opt).map((l) => l.label) : [];
      const values = {
        path: `${D.service} · ${S.path || 'Not chosen yet'}`,
        // The full configuration, so the email shows every choice and which ones still need a price.
        package: S.pkg ? `${pkg(S.pkg).name} (${rangeTop(S.pkg) ? `${money(priceFor(S.pkg, false))}–${money(rangeTop(S.pkg))} range` : `${pkg(S.pkg).from ? 'from ' : ''}${money(priceFor(S.pkg, false))}`})${picks(S.pkg).map((x) => ` · ${x.g.label}: ${x.o.label}${optText(x.o) ? ` (${optText(x.o)})` : ''}`).join('')}` : `${D.service} — not chosen yet, please recommend`,
        vehicle: v ? `${v.short || v.label} (customer's choice; class confirmed before booking)` : 'Not given',
        visitmin: S.pkg && pkg(S.pkg).visitMin ? D.visitMinimum : '',
        recommended: S.rec ? `Recommended: ${S.quote ? `condition-based quote (closest package: ${pkg(S.rec).name})` : pkg(S.rec).name} · Customer selected: ${S.pkg ? pkg(S.pkg).name : 'not yet'}${S.alt ? ` (the ${S.alt})` : ''}` : '',
        status: S.quote && S.pkg === S.rec ? 'Condition-based quote requested: heavier condition with stains, odors or several concerns. Price confirmed after photos or an in-person inspection.' : e ? (e.partial ? `Incomplete: priced items only; quote needed for ${e.lines.filter((l) => l.quote).map((l) => l.label).join(', ')}` : e.point != null ? `Estimate from ${money(e.point)} based on ${e.basis}; ${rangeOf(e)} depending on final condition. Not a final quote.` : e.max ? `Price range shown (${amount(e)}); starts at the lower price, final price within the range depends on cleaning time needed` : e.from ? 'Starting price; confirmed after inspection' : 'Estimate from published prices') : '',
        condition: c ? c.label : 'Not given',
        goal: g ? g.label : '',
        concerns: S.concerns.map((k) => D.concerns.find((x) => x.key === k)?.label).filter(Boolean).join(', '),
        addons: addons.length ? addons.join(', ') : 'None',
        estimate: e ? `${e.point != null ? `Est. from ${money(e.point)} (range ${rangeOf(e)})` : e.max ? amount(e) : `${e.from ? 'From ' : ''}${money(e.total)}`}${e.partial ? ` subtotal; priced after inspection: ${e.lines.filter((l) => l.quote).map((l) => l.label).join(', ')}` : ''}` : '',
        zone: S.zone,
      };
      $$('[data-carry]').forEach((i) => { i.value = values[i.dataset.carry] || ''; });
      const sel = $('[data-req-sel]');
      if (sel) {
        sel.hidden = !S.pkg;
        if (S.pkg) {
          $('[data-carry-text="package"]', sel).textContent = pkg(S.pkg).name;
          $('[data-carry-text="details"]', sel).textContent = [v?.short, addons.length ? `${addons.length} add-on${addons.length > 1 ? 's' : ''}` : ''].filter(Boolean).join(' · ') || 'Vehicle size not set';
          $('[data-carry-text="estimate"]', sel).textContent = e.max ? amount(e) : `${e.from && e.partial ? 'from ' : ''}${money(e.total)}`;
          $('[data-carry-text="kind"]', sel).textContent = e.partial ? 'subtotal + quote' : kindOf(e);
          const vm = $('[data-carry-text="visitmin"]', sel);
          if (vm) { vm.hidden = !pkg(S.pkg).visitMin; vm.textContent = pkg(S.pkg).visitMin ? D.visitMinimum : ''; }
        }
      }
    };

    // ---------- Restore saved state into the inputs, then draw
    $$('input[name="g-vehicle"]', guide).forEach((i) => { i.checked = i.value === S.vehicle; });
    $$('input[name="g-condition"]', guide).forEach((i) => { i.checked = i.value === S.condition; });
    $$('input[name="g-goal"]', guide).forEach((i) => { i.checked = i.value === S.goal; });
    $$('input[name="g-concern"]', guide).forEach((i) => { i.checked = S.concerns.includes(i.value); });
    if (S.zip) { zip.value = S.zip; checkZip(); }
    setMode(S.mode || 'guide');
    if (S.showResult && S.condition) showResult(true); else showStep(Math.min(S.step || 0, steps.length - 1));
    render();
    // "Compare Packages" links (…#packages) open the package list, from this page or another one.
    const openPackages = () => {
      if (location.hash !== '#packages') return;
      setMode('packages');
      requestAnimationFrame(() => root.scrollIntoView({ behavior: smooth, block: 'start' }));
    };
    openPackages();
    window.addEventListener('hashchange', openPackages);
  }

  // ---- Before / after ------------------------------------------------------------
  $$('[data-ba]').forEach((ba) => {
    const r = $('.ba__range', ba);
    const set = () => ba.style.setProperty('--pos', `${r.value}%`);
    r.addEventListener('input', set); set();
  });

  // ---- Lightbox ----------------------------------------------------------------------
  const lb = $('[data-lightbox]');
  if (lb && typeof lb.showModal === 'function') {
    $$('[data-zoom]').forEach((b) => b.addEventListener('click', () => {
      $('[data-lightbox-img]', lb).src = b.dataset.zoom;
      $('[data-lightbox-img]', lb).alt = b.dataset.zoomAlt || '';
      lb.showModal();
    }));
    $('[data-lightbox-close]', lb).addEventListener('click', () => lb.close());
    lb.addEventListener('click', (e) => { if (e.target === lb) lb.close(); });
  } else {
    $$('[data-zoom]').forEach((b) => b.remove());
  }

  // ---- Appointment request drawer -------------------------------------------------
  const req = $('[data-req]');
  if (!req) return;
  const panel = $('[data-req-panel]', req);
  let lastFocus = null;
  const focusables = () => $$('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea', panel).filter((el) => el.offsetParent !== null);
  const inline = req.hasAttribute('data-inline');
  const openReq = () => {
    if (inline) { req.scrollIntoView({ behavior: smooth, block: 'start' }); setTimeout(() => $('input:not([type="hidden"])', panel)?.focus({ preventScroll: true }), 400); return; }
    lastFocus = document.activeElement;
    req.classList.add('is-open');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', $('h2', req).id);
    body.classList.add('has-sheet');
    document.documentElement.style.overflow = 'hidden';
    setTimeout(() => ($('input:not([type="hidden"])', panel) || $('[data-req-close].x', panel))?.focus({ preventScroll: true }), 80);
    track('request_open', { tdr_package: S?.pkg || 'none' });
  };
  const closeReq = () => {
    if (inline) return;
    req.classList.remove('is-open');
    panel.removeAttribute('role'); panel.removeAttribute('aria-modal');
    body.classList.remove('has-sheet');
    document.documentElement.style.overflow = '';
    lastFocus?.focus({ preventScroll: true });
  };
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-open-request], a[href="#request"]');
    if (t) { e.preventDefault(); openReq(); }
  });
  $$('[data-req-close]', req).forEach((b) => b.addEventListener('click', closeReq));
  req.addEventListener('keydown', (e) => {
    if (!req.classList.contains('is-open')) return;
    if (e.key === 'Escape') closeReq();
    if (e.key === 'Tab') {
      const f = focusables(); if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    }
  });

  const serverOk = $('[data-ok-server]', req);
  if (serverOk) {
    openReq(); serverOk.focus({ preventScroll: true });
    // Count the inquiry once: drop Shopify's ?contact_posted flag so a reload or back-navigation can't fire it again.
    try { const u = new URL(location.href); if (u.searchParams.has('contact_posted')) { u.searchParams.delete('contact_posted'); history.replaceState(null, '', u.pathname + u.search + u.hash);
        // tdr-track.js loads after this file; wait for it so a lead after Shopify's bot check isn't lost.
        const lead = () => track('generate_lead', { tdr_method: 'form_post' });
        if (window.tdrTrack) lead(); else document.addEventListener('DOMContentLoaded', lead, { once: true });
      } } catch (_) { /* old browser: skip */ }
    try { Object.keys(sessionStorage).filter((k) => k.startsWith('tdr-build')).forEach((k) => sessionStorage.removeItem(k)); } catch (_) { /* no storage */ }
  }

  const form = $('#tdr-request');
  if (!form || !$('[data-fstep]', form)) return;
  const cfg = JSON.parse($('[data-request-config]', req)?.textContent || '{}');
  const fsteps = $$('[data-fstep]', form);
  const bars = $$('[data-prog] span', form);
  const prev = $('[data-prev]', form);
  const next = $('[data-next]', form);
  const submit = $('[data-submit]', form);
  const sendErr = $('[data-send-error]', form);
  let cur = 0;
  const tomorrow = new Date(Date.now() + 864e5);
  const iso = new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 6e4).toISOString().slice(0, 10);
  $$('input[type="date"]', form).forEach((d) => { d.min = iso; });
  const show = (i) => {
    cur = i;
    fsteps.forEach((s, n) => { s.hidden = n !== i; });
    bars.forEach((b, n) => b.classList.toggle('is-done', n <= i));
    prev.hidden = i === 0;
    next.hidden = i === fsteps.length - 1;
    submit.hidden = i !== fsteps.length - 1;
    $('[data-req-body]', req).scrollTop = 0;
  };
  const validate = (scope) => {
    let bad = null;
    $$('input, select, textarea', scope).forEach((f) => {
      f.parentElement.querySelector(':scope > .field-error')?.remove();
      f.removeAttribute('aria-invalid');
      if (f.type === 'hidden' || f.checkValidity()) return;
      f.setAttribute('aria-invalid', 'true');
      const m = document.createElement('span');
      m.className = 'field-error'; m.id = `${f.id}-err`;
      m.textContent = f.validity.valueMissing ? 'Required' : f.type === 'email' ? 'Check the email address' : 'Please check this';
      f.setAttribute('aria-describedby', m.id);
      f.insertAdjacentElement('afterend', m);
      bad = bad || f;
    });
    if (bad) bad.focus();
    return !bad;
  };
  next.addEventListener('click', () => {
    if (!validate(fsteps[cur])) return;
    track('request_step', { tdr_step: cur + 2 });
    show(cur + 1);
    $('input:not([type="hidden"]), select, textarea', fsteps[cur])?.focus({ preventScroll: true });
  });
  prev.addEventListener('click', () => show(cur - 1));

  const succeed = (payload) => {
    form.hidden = true;
    const ok = $('[data-ok]', req);
    ok.hidden = false;
    if (payload) { const p = $('[data-payload]', ok); p.hidden = false; p.textContent = payload; }
    ok.focus({ preventScroll: true });
    track('generate_lead', { tdr_package: S?.pkg || 'none', tdr_test: Boolean(cfg.testMode) });
    try { Object.keys(sessionStorage).filter((k) => k.startsWith('tdr-build')).forEach((k) => sessionStorage.removeItem(k)); } catch (_) { /* no storage */ }
  };
  // Lead source: first page + referrer + UTM tags of this visit, so every email says where the lead came from.
  const src = (() => {
    let first = null;
    try { first = JSON.parse(sessionStorage.getItem('tdr-src') || 'null'); } catch (_) { /* no storage */ }
    if (!first) {
      const q = new URLSearchParams(location.search);
      const utm = ['utm_source', 'utm_medium', 'utm_campaign'].map((k) => q.get(k)).filter(Boolean).join(' / ');
      let ref = '';
      try { ref = document.referrer ? new URL(document.referrer).hostname : ''; } catch (_) { /* bad referrer */ }
      first = { utm, ref: ref && ref !== location.hostname ? ref : '', landing: location.pathname };
      try { sessionStorage.setItem('tdr-src', JSON.stringify(first)); } catch (_) { /* no storage */ }
    }
    const known = [[/(^|\.)google\./, 'Google (search or Maps)'], [/bing\./, 'Bing'], [/facebook\.|fb\./, 'Facebook'], [/instagram\./, 'Instagram'],
      [/yelp\./, 'Yelp'], [/nextdoor\./, 'Nextdoor'], [/duckduckgo\./, 'DuckDuckGo'], [/yahoo\./, 'Yahoo']];
    const named = first.ref && (known.find(([re]) => re.test(first.ref)) || [])[1];
    const channel = first.utm ? `Tagged link: ${first.utm}` : named || (first.ref ? `Referral: ${first.ref}` : 'Direct (typed, bookmark or app)');
    return [channel, `landed on ${first.landing}`, `sent from ${location.pathname}`].join(' · ');
  })();
  const srcInput = $('[data-lead-source]', form);
  if (srcInput) srcInput.value = src;
  // Ad click and Analytics IDs let a booked job be credited to its ad / visit later. Read at send time
  // (tdr-site.js stores the click ID after this file runs). Empty when absent.
  const fillIds = () => {
    const clickInput = $('[data-ad-click]', form);
    if (clickInput) { try { const c = JSON.parse(localStorage.getItem('tdr-click') || 'null'); clickInput.value = c && Date.now() - c.at < 90 * 864e5 ? c.id : ''; } catch (_) { /* no storage */ } }
    const gaInput = $('[data-ga-id]', form);
    if (gaInput) { const m = document.cookie.match(/(?:^|; )_ga=GA\d\.\d\.(\d+\.\d+)/); gaInput.value = m ? m[1] : ''; }
  };

  // Empty selection fields (e.g. on quote-only pages) aren't sent, so the email stays clean.
  const pruneEmpty = () => fillIds() || $$('input[type="hidden"][data-carry], [data-ad-click], [data-ga-id]', form).forEach((i) => { i.disabled = !i.value; });
  form.addEventListener('submit', async (e) => {
    sendErr.hidden = true;
    pruneEmpty();
    if (!validate(fsteps[cur])) { e.preventDefault(); return; }
    e.preventDefault();
    if (cfg.testMode) {
      const data = {};
      new FormData(form).forEach((v, k) => { const key = k.replace(/^contact\[(.*)\]$/, '$1'); if (!['form_type', 'utf8'].includes(key) && v) data[key] = v; });
      succeed(JSON.stringify(data, null, 2));
      return;
    }
    form.classList.add('is-sending');
    submit.textContent = 'Sending…';
    try {
      const res = await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' });
      if (res.url.includes('contact_posted=true')) { succeed(); return; }
      if (res.ok) {
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const err = doc.querySelector('#tdr-request .banner--error');
        if (err) { sendErr.textContent = err.textContent.trim(); sendErr.hidden = false; return; }
      }
      // Shopify's bot check, a captcha or anything unexpected: hand over to a normal post so
      // Shopify can verify the visitor and bring them back to the success message.
      form.submit();
    } catch (_) {
      form.submit();
    } finally {
      form.classList.remove('is-sending');
      submit.textContent = 'Send request';
    }
  });
  show(0);
})();
